import {
  Card, Row, Col, Tag, Button, Tabs, Input, Space, Avatar,
  Progress, Typography, Tooltip, Divider, Empty, Spin, message, Modal,
} from 'antd';
import {
  PlusOutlined, EditOutlined, StopOutlined, DeleteOutlined, SearchOutlined,
  CalendarOutlined, EnvironmentOutlined, MedicineBoxOutlined,
  BankOutlined, SyncOutlined, CheckCircleOutlined, CloseCircleOutlined,
  TeamOutlined, ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { eventApi } from '../../../api/event.api';
import EventForm from '../components/EventForm';
import { showConfirm } from '../../../components/common/ConfirmModal';
import { formatDate } from '../../../utils/helpers';
import useDebounce from '../../../hooks/useDebounce';
import { usePermission } from '../../../hooks/usePermission';
import { useAuthStore } from '../../../store/auth.store';

const { Title, Text } = Typography;
const PAGE_SIZE         = 12;
// Auto-scroll loads up to this many chunks silently.
// Beyond this threshold a "Load More" button takes over so the user
// consciously decides to load 100+ events rather than filling the DOM silently.
const AUTO_SCROLL_PAGES = 3; // 3 × 12 = 36 cards auto-loaded

const computeStatus = (eventDate, storedStatus) => {
  if (storedStatus) { return storedStatus; }
  const now = dayjs();
  const d   = dayjs(eventDate);
  if (d.isSame(now, 'day'))  { return 'ongoing'; }
  if (d.isAfter(now, 'day')) { return 'upcoming'; }
  return 'completed';
};

const STATUS_CFG = {
  upcoming:  { label: 'Upcoming',    color: 'var(--wl-primary)', bg: 'var(--wl-primary-pale)', icon: <CalendarOutlined /> },
  ongoing:   { label: 'In Progress', color: '#52c41a', bg: '#f6ffed', icon: <SyncOutlined /> },
  completed: { label: 'Completed',   color: '#595959', bg: '#f5f5f5', icon: <CheckCircleOutlined /> },
  cancelled: { label: 'Cancelled',   color: '#ff4d4f', bg: '#fff2f0', icon: <CloseCircleOutlined /> },
};

// ─── Single event card ─────────────────────────────────────────────
const EventCard = ({ event, isAdmin, onEdit, onCancel, onDelete, onNavigate }) => {
  const status   = computeStatus(event.eventDate, event.status);
  const cfg      = STATUS_CFG[status];
  const doctors  = event.assignedDoctors || [];
  const totalReg = event.totalRegistered || 0;
  const totalChk = event.totalChecked    || 0;
  const pct      = totalReg > 0 ? Math.round((totalChk / totalReg) * 100) : 0;

  const showAdminActions = isAdmin;

  return (
    <Card
      hoverable
      onClick={() => onNavigate(event.id)}
      style={{
        borderRadius: 16,
        border: '1.5px solid var(--wl-primary-pale)',
        boxShadow: '0 2px 12px rgba(var(--wl-primary-rgb),0.07)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
      }}
      styles={{ body: { padding: 20, display: 'flex', flexDirection: 'column', flex: 1 } }}
    >
      {/* Status + Date */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <Tag
          icon={cfg.icon}
          style={{
            background: cfg.bg,
            color: cfg.color,
            border: `1px solid ${cfg.color}40`,
            borderRadius: 9999,
            fontWeight: 600,
            fontSize: 11,
            padding: '2px 10px',
          }}
        >
          {cfg.label}
        </Tag>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {formatDate(event.eventDate, 'DD MMM YYYY')}
        </Text>
      </div>

      {/* Title */}
      <Title
        level={5}
        ellipsis={{ tooltip: event.title }}
        style={{ marginBottom: 10, color: 'var(--wl-primary)', lineHeight: 1.4 }}
      >
        {event.title}
      </Title>

      {/* Meta */}
      <Space direction="vertical" size={3} style={{ marginBottom: 12 }}>
        <Text style={{ fontSize: 12, color: '#555' }}>
          <EnvironmentOutlined style={{ color: 'var(--wl-primary)', marginRight: 5 }} />
          {event.location || '—'}
        </Text>
        <Tooltip title={event.school?.name?.length > 30 ? event.school.name : undefined}>
          <Text style={{ fontSize: 12, color: '#555', display: 'flex', alignItems: 'center' }}>
            <BankOutlined style={{ color: 'var(--wl-primary)', marginRight: 5, flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {event.school?.name || '—'}
            </span>
          </Text>
        </Tooltip>
        <Tooltip title={event.clinic?.name?.length > 30 ? event.clinic.name : undefined}>
          <Text style={{ fontSize: 12, color: '#555', display: 'flex', alignItems: 'center' }}>
            <MedicineBoxOutlined style={{ color: 'var(--wl-primary)', marginRight: 5, flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {event.clinic?.name || '—'}
            </span>
          </Text>
        </Tooltip>
      </Space>

      {/* Doctors */}
      <div style={{ marginBottom: 12 }}>
        {doctors.length > 0 ? (
          <>
            <Avatar.Group maxCount={3} size="small">
              {doctors.map((d) => (
                <Tooltip key={d.id || d._id} title={d.name}>
                  <Avatar size="small" style={{ background: 'var(--wl-primary)', fontSize: 11 }}>
                    {d.name?.charAt(0)?.toUpperCase()}
                  </Avatar>
                </Tooltip>
              ))}
            </Avatar.Group>
            <Text style={{ fontSize: 11, color: '#aaa', marginLeft: 6 }}>
              <TeamOutlined style={{ marginRight: 3 }} />
              {doctors.length} doctor{doctors.length !== 1 ? 's' : ''}
            </Text>
          </>
        ) : (
          <Text style={{ fontSize: 11, color: '#bbb', fontStyle: 'italic' }}>
            <TeamOutlined style={{ marginRight: 4 }} />
            No doctors assigned
          </Text>
        )}
      </div>
      {showAdminActions && (
        <>
          <Divider style={{ margin: '0 0 12px' }} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            {status !== 'cancelled' && (
              <Tooltip title="Edit event">
                <Button
                  shape="circle"
                  size="small"
                  icon={<EditOutlined />}
                  style={{ color: 'var(--wl-primary)', borderColor: 'var(--wl-primary)', background: '#ffffff' }}
                  onClick={(e) => { e.stopPropagation(); onEdit(event); }}
                />
              </Tooltip>
            )}
            {status !== 'cancelled' && status !== 'completed' && (
              <Tooltip title="Cancel event">
                <Button
                  shape="circle"
                  size="small"
                  danger
                  icon={<StopOutlined />}
                  style={{ color: '#ff4d4f', borderColor: '#ff4d4f', background: '#fff2f0' }}
                  onClick={(e) => { e.stopPropagation(); onCancel(event); }}
                />
              </Tooltip>
            )}
            {(status === 'upcoming' || status === 'cancelled') && (
              <Tooltip title="Delete event">
                <Button
                  shape="circle"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  style={{ color: '#ff4d4f', borderColor: '#ff4d4f', background: '#fff2f0' }}
                  onClick={(e) => { e.stopPropagation(); onDelete(event); }}
                />
              </Tooltip>
            )}
          </div>
        </>
      )}
    </Card>
  );
};

// ─── Main page ─────────────────────────────────────────────────────
const EventList = () => {
  const [formOpen,    setFormOpen]    = useState(false);
  const [editRecord,  setEditRecord]  = useState(null);
  const [searchParams] = useSearchParams();
  const [activeTab,   setActiveTab]   = useState(() => {
    const tab = searchParams.get('tab');
    return ['upcoming', 'ongoing', 'completed', 'cancelled'].includes(tab) ? tab : 'all';
  });
  const [searchInput, setSearchInput] = useState('');

  const sentinelRef = useRef(null);
  const navigate    = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin, isDoctor, isSchoolStaff } = usePermission();
  const { user } = useAuthStore();

  const debouncedSearch = useDebounce(searchInput, 300);

  // Query params WITHOUT page — useInfiniteQuery injects pageParam itself
  const queryParams = useMemo(() => {
    const params = { search: debouncedSearch, limit: PAGE_SIZE };
    if (activeTab !== 'all')           { params.status = activeTab; }
    if (isSchoolStaff && user?.school) { params.school = user.school; }
    if (isSchoolStaff && user?.staff)  { params.staff = user.staff; }
    if (isDoctor && user?.doctor)      { params.doctor = user.doctor; }
    return params;
  }, [debouncedSearch, activeTab, isSchoolStaff, isDoctor, user?.school, user?.staff, user?.doctor]);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey:         ['events', queryParams],
    queryFn:          ({ pageParam = 1 }) => eventApi.getAll({ ...queryParams, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, limit, total } = lastPage?.meta || {};
      if (!page || !limit || !total) return undefined;
      return page * limit < total ? page + 1 : undefined;
    },
  });

  // Flatten all loaded pages into a single list
  const events      = useMemo(() => data?.pages.flatMap((p) => p.data || []) || [], [data]);
  const total       = data?.pages[0]?.meta?.total || 0;
  const pagesLoaded = data?.pages.length || 0;

  // Auto-scroll only for the first AUTO_SCROLL_PAGES chunks.
  // Once past the threshold the sentinel is dormant and the user
  // sees a "Load More" button instead — prevents silently loading 100+ DOM nodes.
  const withinAutoScrollThreshold = pagesLoaded < AUTO_SCROLL_PAGES;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage && withinAutoScrollThreshold) {
          fetchNextPage();
        }
      },
      { rootMargin: '300px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, withinAutoScrollThreshold]);

  const createMutation = useMutation({
    mutationFn: eventApi.create,
    onSuccess:  () => { queryClient.invalidateQueries(['events']); message.success('Event created'); setFormOpen(false); },
    onError:    (err) => message.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data: d }) => eventApi.update(id, d),
    onSuccess:  () => {
      queryClient.invalidateQueries(['events']);
      message.success('Event updated');
      setFormOpen(false);
      setEditRecord(null);
    },
    onError: (err) => message.error(err.message),
  });

  const cancelMutation = useMutation({
    mutationFn: eventApi.cancel,
    onSuccess:  () => { queryClient.invalidateQueries(['events']); message.success('Event cancelled'); },
    onError:    (err) => message.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => eventApi.delete(id),
    onSuccess:  () => { queryClient.invalidateQueries(['events']); message.success('Event deleted'); },
    onError:    (err) => message.error(err.message),
  });

  const tabItems = [
    { key: 'all',       label: 'All' },
    { key: 'upcoming',  label: 'Upcoming' },
    { key: 'ongoing',   label: 'In Progress' },
    { key: 'completed', label: 'Completed' },
    { key: 'cancelled', label: 'Cancelled' },
  ];

  // Scroll to top + reset when tab or search changes
  const handleTabChange = (key) => {
    setActiveTab(key);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const handleSearch = (e) => setSearchInput(e.target.value);

  const handleEdit   = useCallback((event) => { setEditRecord(event); setFormOpen(true); }, []);
  const handleCancel = useCallback((event) => showConfirm({
    title:   'Cancel Event',
    content: `Cancel "${event.title}"? This cannot be undone.`,
    okText:  'Cancel Event',
    okType:  'danger',
    onOk:    () => cancelMutation.mutate(event.id),
  }), [cancelMutation]);

  const handleDelete = useCallback((event) => {
    const count = event.totalRegistered || 0;
    if (count > 0) {
      Modal.warning({
        title:   'Cannot Delete Event',
        icon:    <ExclamationCircleOutlined style={{ color: '#fa8c16' }} />,
        content: (
          <div style={{ paddingTop: 4 }}>
            <p style={{ marginBottom: 8 }}>
              This event has{' '}
              <strong style={{ color: 'var(--wl-primary)' }}>
                {count} {count === 1 ? 'child' : 'children'}
              </strong>{' '}
              assigned to it.
            </p>
            <p style={{ marginBottom: 0, color: '#555' }}>
              Please remove all assigned children from this event before deleting it.
            </p>
          </div>
        ),
        okText:  'Got it',
      });
      return;
    }
    Modal.confirm({
      title:   'Delete Event',
      icon:    <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      content: (
        <div style={{ paddingTop: 4 }}>
          <p style={{ marginBottom: 8 }}>
            Are you sure you want to permanently delete{' '}
            <strong>"{event.title}"</strong>?
          </p>
          <p style={{ color: '#ff4d4f', marginBottom: 0 }}>
            This action cannot be undone.
          </p>
        </div>
      ),
      okText:        'Delete',
      okButtonProps: { danger: true },
      cancelText:    'Cancel',
      onOk:          () => deleteMutation.mutate(event.id),
    });
  }, [deleteMutation]);

  const handleFormSubmit = (values) => {
    if (editRecord) { updateMutation.mutate({ id: editRecord.id, data: values }); }
    else            { createMutation.mutate(values); }
  };

  const subtitle = isDoctor
    ? 'Your assigned events'
    : isSchoolStaff
      ? "Your school's events"
      : 'Manage dental camp events';

  return (
    <div>
      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <Title level={4} style={{ margin: 0, color: 'var(--wl-primary)' }}>Events</Title>
          <Text type="secondary">{subtitle}</Text>
        </div>
        {isAdmin && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{ background: 'linear-gradient(135deg, var(--wl-primary-light), var(--wl-primary))', border: 'none' }}
            onClick={() => { setEditRecord(null); setFormOpen(true); }}
          >
            Create Event
          </Button>
        )}
      </div>

      {/* Search bar */}
      <div style={{
        background: '#fff', borderRadius: 12, padding: '10px 16px',
        marginBottom: 12, boxShadow: '0 2px 8px rgba(var(--wl-primary-rgb),0.06)',
        border: '1px solid var(--wl-primary-pale)',
      }}>
        <Input
          prefix={<SearchOutlined style={{ color: 'var(--wl-text-muted)' }} />}
          placeholder="Search by event title or location…"
          value={searchInput}
          onChange={handleSearch}
          allowClear
          style={{ borderRadius: 9999, borderColor: 'var(--wl-primary-pale)', maxWidth: 400 }}
        />
      </div>

      {/* Status tabs */}
      <div style={{
        background: '#fff', borderRadius: 12, padding: '0 16px',
        marginBottom: 20, boxShadow: '0 2px 8px rgba(var(--wl-primary-rgb),0.06)',
        border: '1px solid var(--wl-primary-pale)',
      }}>
        <Tabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} size="small" />
      </div>

      {/* Cards grid */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>
      ) : events.length === 0 ? (
        <Empty
          description={`No ${activeTab === 'all' ? '' : activeTab} events found`}
          style={{ padding: 80 }}
        />
      ) : (
        <>
          <Row gutter={[16, 16]}>
            {events.map((event) => (
              <Col xs={24} sm={24} md={12} lg={8} key={event.id}>
                <EventCard
                  event={event}
                  isAdmin={isAdmin}
                  onEdit={handleEdit}
                  onCancel={handleCancel}
                  onDelete={handleDelete}
                  onNavigate={(id) => navigate(`/events/${id}`)}
                />
              </Col>
            ))}
          </Row>

          {/* Showing X of Y counter */}
          {total > 0 && (
            <div style={{ textAlign: 'center', marginTop: 20, color: '#999', fontSize: 12 }}>
              Showing <strong style={{ color: 'var(--wl-primary)' }}>{events.length}</strong> of{' '}
              <strong style={{ color: 'var(--wl-primary)' }}>{total}</strong> events
            </div>
          )}

          {/* Invisible sentinel — active only within the auto-scroll threshold */}
          <div ref={sentinelRef} style={{ height: 1, marginTop: 8 }} />

          {/* Spinner while auto-loading next chunk */}
          {isFetchingNextPage && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <Spin />
              <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 13 }}>
                Loading more…
              </Text>
            </div>
          )}

          {/* "Load More" button — shown once past the auto-scroll threshold */}
          {hasNextPage && !withinAutoScrollThreshold && !isFetchingNextPage && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <Button
                onClick={() => fetchNextPage()}
                style={{
                  borderColor: 'var(--wl-primary-light)',
                  color:       'var(--wl-primary-light)',
                  borderRadius: 9999,
                  padding:     '0 32px',
                  fontWeight:  600,
                }}
              >
                Load More — {total - events.length} remaining
              </Button>
            </div>
          )}

          {/* All loaded */}
          {!hasNextPage && total > PAGE_SIZE && (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#bbb', fontSize: 13 }}>
              All {total} events loaded
            </div>
          )}
        </>
      )}

      <EventForm
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditRecord(null); }}
        onSubmit={handleFormSubmit}
        initialValues={editRecord}
        loading={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  );
};

export default EventList;
